import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SiteLayout } from "@/components/site/Layout";
import { useSeo } from "@/context/ContentContext";
import { IntakeForm, EMPTY_INTAKE } from "@/components/booking/IntakeForm";
import { SlotPicker } from "@/components/booking/SlotPicker";
import { useAuth } from "@/context/AuthContext";
import { api, gbp, formatApiError, longDate } from "@/lib/api";

const STEPS = ["Treatment", "Practitioner", "Date & time", "Intake form", "Review & pay"];

const Stepper = ({ step }) => (
  <ol className="flex flex-wrap gap-2 mb-10" data-testid="booking-stepper">
    {STEPS.map((s, i) => (
      <li key={s} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm border ${i === step ? "bg-primary text-white border-primary" : i < step ? "bg-primary/10 text-primary border-primary/20" : "bg-card border-border text-muted-foreground"}`}>
        {i < step ? <Check className="h-3.5 w-3.5" /> : <span className="text-xs font-semibold">{i + 1}</span>}{s}
      </li>
    ))}
  </ol>
);

const Choice = ({ active, onClick, testid, children }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`w-full text-left rounded-2xl border p-4 transition-colors ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card hover:border-primary/40"}`}>
    {children}
  </button>
);

export default function Book() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [intake, setIntake] = useState(EMPTY_INTAKE);
  const [paying, setPaying] = useState(false);

  const serviceId = params.get("service");
  const pracId = params.get("practitioner");
  const date = params.get("date");
  const time = params.get("time");
  const intakeDone = params.get("intake") === "1";

  useEffect(() => {
    api.get(`/clinics/${slug}`).then((r) => setClinic(r.data));
  }, [slug]);

  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };

  const service = clinic?.services.find((s) => s.id === serviceId);
  const pracs = useMemo(() => clinic?.practitioners.filter((p) => !serviceId || p.service_ids.includes(serviceId)) || [], [clinic, serviceId]);
  const prac = pracs.find((p) => p.id === pracId);
  const step = !service ? 0 : !prac ? 1 : !date || !time ? 2 : !intakeDone ? 3 : 4;

  const pay = async () => {
    setPaying(true);
    try {
      const { data: b } = await api.post("/bookings", {
        clinic_id: clinic.id, service_id: service.id, practitioner_id: prac.id, date, start: time, intake,
      });
      const { data } = await api.post("/payments/checkout", { booking_id: b.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 409) set({ time: null, intake: null });
      setPaying(false);
    }
  };
  useSeo("book", { clinic: clinic?.name || "", city: clinic?.city || "" });

  if (!clinic) return <SiteLayout><div className="p-20 flex justify-center"><Loader2 className="animate-spin text-primary" /></div></SiteLayout>;

  const back = () => {
    if (step === 4) set({ intake: null });
    else if (step === 3) set({ time: null });
    else if (step === 2) set({ practitioner: null, date: null, time: null });
    else if (step === 1) set({ service: null });
    else nav(`/clinic/${slug}`);
  };

  return (
    <SiteLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button onClick={back} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="booking-back-button">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <p className="eyebrow">{clinic.name}</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-2 mb-8">Book an appointment</h1>
        <Stepper step={step} />
        <div className="grid lg:grid-cols-[1fr_300px] gap-8">
          <div className="fade-up min-w-0" key={step}>
            {step === 0 && (
              <div className="grid gap-3">
                {clinic.services.map((s) => (
                  <Choice key={s.id} onClick={() => set({ service: s.id, practitioner: null, time: null })} testid={`service-select-button-${s.id}`}>
                    <div className="flex justify-between gap-4">
                      <div><p className="font-display font-bold">{s.name}</p><p className="text-sm text-muted-foreground">{s.duration} min · {s.description}</p></div>
                      <p className="font-semibold shrink-0">{gbp(s.price)}</p>
                    </div>
                  </Choice>
                ))}
              </div>
            )}
            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-3">
                {pracs.map((p) => (
                  <Choice key={p.id} onClick={() => set({ practitioner: p.id, time: null })} testid={`practitioner-select-button-${p.id}`}>
                    <div className="flex gap-3 items-center">
                      <img src={p.image} alt={p.name} className="h-14 w-14 rounded-xl object-cover" />
                      <div><p className="font-display font-bold">{p.name}</p><p className="text-sm text-primary">{p.title}</p></div>
                    </div>
                  </Choice>
                ))}
                {pracs.length === 0 && <p className="text-muted-foreground">No practitioner offers this treatment yet.</p>}
              </div>
            )}
            {step === 2 && <SlotPicker practitionerId={prac.id} serviceId={service.id} date={date} time={time} onChange={set} />}
            {step === 3 && (user?.role === "patient" ? (
              <IntakeForm value={intake} onChange={setIntake} onSubmit={() => set({ intake: "1" })} />
            ) : (
              <div className="rounded-2xl border border-border bg-card p-6 space-y-4" data-testid="booking-login-prompt">
                <p className="font-display font-bold text-lg">{user ? "Patient account needed" : "Log in to continue"}</p>
                <p className="text-sm text-muted-foreground">
                  {user ? "You're signed in with a clinic or admin account. Log in with a patient account to book." : "Create a free patient account or log in to fill in your intake form and pay."}
                </p>
                {!user && (
                  <div className="flex gap-3">
                    <Button className="rounded-full" asChild data-testid="booking-login-button"><Link to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}>Log in</Link></Button>
                    <Button variant="outline" className="rounded-full" asChild data-testid="booking-signup-button"><Link to={`/signup?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}>Create account</Link></Button>
                  </div>
                )}
              </div>
            ))}
            {step === 4 && (
              <div className="rounded-2xl border border-border bg-card p-6 space-y-5" data-testid="booking-review">
                <p className="font-display font-bold text-lg">Review and pay</p>
                <div className="text-sm space-y-1 text-slate-600">
                  <p><span className="text-muted-foreground">Reason for visit:</span> {intake.reason || "—"}</p>
                  <p><span className="text-muted-foreground">Emergency contact:</span> {intake.emergency_name || "—"} {intake.emergency_phone}</p>
                </div>
                {!intake.reason && <p className="text-sm text-amber-700">Your intake answers were cleared — go back and fill them in again.</p>}
                <Button size="lg" className="rounded-full w-full h-12" onClick={pay} disabled={paying || !intake.reason || !intake.consent} data-testid="stripe-checkout-pay-button">
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Pay {gbp(service.price)} securely</>}
                </Button>
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4" /> Card payment handled by Stripe. Your slot is held for 30 minutes.</p>
              </div>
            )}
          </div>
          <aside className="rounded-2xl border border-border bg-card p-5 h-fit space-y-3 text-sm" data-testid="booking-summary">
            <p className="font-display font-bold">Summary</p>
            <p><span className="text-muted-foreground">Treatment</span><br />{service ? `${service.name} · ${service.duration} min` : "—"}</p>
            <p><span className="text-muted-foreground">Practitioner</span><br />{prac?.name || "—"}</p>
            <p><span className="text-muted-foreground">When</span><br />{date && time ? `${longDate(date)} at ${time}` : "—"}</p>
            <p className="pt-3 border-t border-border flex justify-between font-semibold"><span>Total</span><span>{service ? gbp(service.price) : "—"}</span></p>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}
